import React, { useEffect, useState } from 'react';
import { useTranslation } from '../context/TranslationContext';
import '../styles/News.css';
import { fetchNewsItems } from '../helpers/contentfulHelper';
import ReactMarkdown from 'react-markdown';
import Breadcrumb from './Breadcrumb';
import '../styles/Views.css';

const markdownComponents = {
  a: ({ children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

const News = () => {
  const { language, t } = useTranslation();
  const [newsItems, setNewsItems] = useState([]);
  const [openItems, setOpenItems] = useState([]);

  useEffect(() => {
    const loadNews = async () => {
      const items = await fetchNewsItems();
      setNewsItems(items);
    };
    loadNews();
  }, []);

  const isUpdate = ({ fields }) =>
    (fields.tags || []).some(
      tag => typeof tag === 'string' && tag.trim().toLowerCase() === 'update'
    );

  const communications = newsItems.filter(isUpdate);
  const news = newsItems.filter(item => !isUpdate(item));

  const getTitle = fields =>
    language === 'pt' ? fields.titlePt || fields.title : fields.title;

  const getDescription = fields =>
    language === 'pt'
      ? fields.descriptionPt || fields.description
      : fields.description;

  const getPreview = fields => {
    const description = getDescription(fields) || '';
    const plainText = description
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
      .replace(/^[\s>#-]+/gm, '')
      .replace(/[*_~`]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (plainText.length <= 120) return plainText;

    const shortened = plainText.slice(0, 120);
    const lastSpace = shortened.lastIndexOf(' ');
    return `${shortened.slice(0, lastSpace > 80 ? lastSpace : 120)}...`;
  };

  const formatDate = ({ sys, fields }) =>
    new Date(fields.date || sys.createdAt).toLocaleDateString(language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const toggleItem = id => {
    setOpenItems(current =>
      current.includes(id)
        ? current.filter(openId => openId !== id)
        : [...current, id]
    );
  };

  const renderCollapsibleItems = items => (
    <div className="communications-list">
      {items.map(item => {
        const { sys, fields } = item;
        const isOpen = openItems.includes(sys.id);
        const preview = getPreview(fields);

        return (
          <article
            key={sys.id}
            className={`communication-row ${isOpen ? 'is-open' : ''}`}
          >
            <button
              type="button"
              className="communication-trigger"
              onClick={() => toggleItem(sys.id)}
              aria-expanded={isOpen}
              aria-controls={`communication-content-${sys.id}`}
            >
              <span className="communication-summary">
                <span className="communication-title">{getTitle(fields)}</span>
                {!isOpen && preview && (
                  <span className="communication-preview">{preview}</span>
                )}
              </span>
              <span className="communication-trigger-meta">
                <time className="communication-date">{formatDate(item)}</time>
                <span className="communication-icon" aria-hidden="true">
                  {isOpen ? '−' : '+'}
                </span>
              </span>
            </button>

            {isOpen && (
              <div
                id={`communication-content-${sys.id}`}
                className="communication-content news-description"
              >
                <ReactMarkdown components={markdownComponents}>
                  {getDescription(fields)}
                </ReactMarkdown>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );

  return (
    <div className="portal-page news-container">
      <div className="portal-page-breadcrumb">
        <Breadcrumb
          items={[
            { label: t('breadcrumb.home'), link: '/' },
            { label: t('breadcrumb.communicationsNews') },
          ]}
        />
      </div>

      <header className="portal-page-header">
        <h2 className="portal-page-title">{t('news_page.communicationsHeading')}</h2>
      </header>

      <section className="portal-page-panel communications-panel">
        {communications.length === 0 ? (
          <p className="news-empty-state">{t('news_page.noCommunications')}</p>
        ) : (
          renderCollapsibleItems(communications)
        )}
      </section>

      <div className="news-section-header">
        <h2 className="news-section-title">{t('news_page.newsHeading')}</h2>
      </div>

      <section className="portal-page-panel communications-panel">
        {news.length === 0 ? (
          <p className="news-empty-state">{t('news_page.noNews')}</p>
        ) : (
          renderCollapsibleItems(news)
        )}
      </section>
    </div>
  );
};

export default News;
